import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, CircularProgress, Divider, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { deleteArchivoProyecto, descargarArchivoProyecto, getArchivosProyecto, postArchivoProyecto } from '../../services/apis';
import { advertenciaConfirmar } from '../../utils/componentes-utiles/advertencias';

export const ArchivosProyecto = ({ idProyecto }) => {
    const inputRef = useRef(null);
    const [loaded, setLoaded] = useState(false);
    const [subiendo, setSubiendo] = useState(false);
    const [archivos, setArchivos] = useState([]);
    const [error, setError] = useState('');

    const cargarArchivos = async () => {
        if (!idProyecto && idProyecto !== 0) return;

        setError('');
        const response = await getArchivosProyecto(idProyecto);

        if (response?.success) {
            setArchivos(response.data || []);
        } else {
            setError(response?.message || 'No se han podido cargar los archivos del proyecto.');
        }

        setLoaded(true);
    };

    useEffect(() => {
        cargarArchivos();
    }, [idProyecto]);

    const formatearTamano = (bytes) => {
        const size = Number(bytes) || 0;

        if (size < 1024) return `${size} B`;
        if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;

        return `${(size / (1024 * 1024)).toFixed(1)} MB`;
    };

    const formatearFecha = (fecha) => {
        if (!fecha) return '';

        const fechaObj = new Date(fecha);
        if (Number.isNaN(fechaObj.getTime())) return fecha;

        return fechaObj.toLocaleDateString('es-ES');
    };

    const seleccionarArchivo = () => {
        inputRef.current?.click();
    };

    const subirArchivo = async (event) => {
        const archivo = event.target.files?.[0];

        if (!archivo) return;

        setSubiendo(true);
        setError('');

        const response = await postArchivoProyecto(idProyecto, archivo);

        if (response?.success) {
            await cargarArchivos();
        } else {
            setError(response?.message || 'No se ha podido subir el archivo.');
        }

        event.target.value = '';
        setSubiendo(false);
    };

    const descargarArchivo = async (archivo) => {
        await descargarArchivoProyecto(idProyecto, archivo);
    };

    const eliminarArchivo = async (archivo) => {
        const confirmado = await advertenciaConfirmar(
            'Eliminar archivo',
            `El archivo "${archivo.nombre_original}" dejara de mostrarse en el proyecto.`
        );

        if (!confirmado) return;

        const response = await deleteArchivoProyecto(idProyecto, archivo.id);

        if (response?.success) {
            cargarArchivos();
        } else {
            setError(response?.message || 'No se ha podido eliminar el archivo.');
        }
    };

    return (
        <Grid container xs={12} justifyContent="center" sx={{ mb: 3 }}>
            <Grid item xs={11} lg={9}>
                <Card sx={{ background: 'var(--fondoTarjeta)', borderRadius: 2 }}>
                    <CardContent>
                        <Grid container alignItems="center" justifyContent="space-between" gap={1}>
                            <Box display="flex" alignItems="center" gap={1}>
                                <AttachFileIcon color="primary" />
                                <Typography variant="h6">Archivos del proyecto</Typography>
                            </Box>

                            <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                startIcon={<CloudUploadIcon />}
                                onClick={seleccionarArchivo}
                                disabled={subiendo}
                            >
                                Subir archivo
                            </Button>

                            <input
                                ref={inputRef}
                                type="file"
                                hidden
                                onChange={subirArchivo}
                            />
                        </Grid>

                        {error && (
                            <Alert severity="warning" sx={{ mt: 2 }}>
                                {error}
                            </Alert>
                        )}

                        <Divider sx={{ my: 1.5 }} />

                        {!loaded && (
                            <Grid container justifyContent="center" sx={{ py: 2 }}>
                                <CircularProgress size={28} />
                            </Grid>
                        )}

                        {loaded && archivos.length === 0 && !error && (
                            <Typography variant="body2" color="text.secondary" textAlign="center">
                                No hay archivos subidos en este proyecto.
                            </Typography>
                        )}

                        {loaded && archivos.length > 0 && (
                            <Grid container spacing={1}>
                                {archivos.map((archivo) => (
                                    <Grid item xs={12} key={archivo.id}>
                                        <Box
                                            sx={{
                                                display: 'grid',
                                                gridTemplateColumns: { xs: '1fr auto', md: 'minmax(0, 1fr) 140px 120px 88px' },
                                                gap: 1,
                                                alignItems: 'center',
                                                px: 1,
                                                py: 0.75,
                                                border: '1px solid rgba(0,0,0,0.12)',
                                                borderRadius: 1
                                            }}
                                        >
                                            <Box display="flex" alignItems="center" gap={1} minWidth={0}>
                                                <InsertDriveFileIcon color="primary" fontSize="small" />
                                                <Typography
                                                    variant="body2"
                                                    fontWeight={600}
                                                    sx={{
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }}
                                                >
                                                    {archivo.nombre_original}
                                                </Typography>
                                            </Box>

                                            <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', md: 'block' } }}>
                                                {archivo.nombre_usuario_subida || 'Sin usuario'}
                                            </Typography>

                                            <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', md: 'block' } }}>
                                                {formatearFecha(archivo.fecha_subida)} · {formatearTamano(archivo.tamano)}
                                            </Typography>

                                            <Box display="flex" justifyContent="end">
                                                <Tooltip title="Descargar">
                                                    <IconButton size="small" color="primary" onClick={() => descargarArchivo(archivo)}>
                                                        <DownloadIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Eliminar">
                                                    <IconButton size="small" color="error" onClick={() => eliminarArchivo(archivo)}>
                                                        <DeleteForeverIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                    </CardContent>
                </Card>
            </Grid>
        </Grid>
    );
};
